import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Table, Input, Select, Button, Avatar, Tag, Card, Spin,
  DatePicker, Space, Typography, Row, Col, Statistic, Tooltip,
  Switch, Popconfirm, message,
} from 'antd';
import {
  SearchOutlined, ReloadOutlined, CrownOutlined,
  UserOutlined, CheckCircleOutlined, CloseCircleOutlined, GlobalOutlined,
  BarChartOutlined, WhatsAppOutlined,
} from '@ant-design/icons';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as ChartTooltip, ResponsiveContainer, Cell,
} from 'recharts';
import dayjs from 'dayjs';
import { getSubscriptionsApi, remindSubscribeApi } from '../../api/admin';
import { USER_IMAGE } from '../../utils/constants';
import { formatDate } from '../../utils/formatters';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const PRIMARY = '#1a6b2a';

const WEB_ACCESS_OPTIONS = [
  { value: '',  label: 'All'         },
  { value: '1', label: 'Web Access'  },
  { value: '0', label: 'No Web Access' },
];

function expireColor(dateStr) {
  if (!dateStr) return 'default';
  const d = dayjs(dateStr);
  if (d.isBefore(dayjs())) return 'error';
  if (d.isBefore(dayjs().add(30, 'day'))) return 'warning';
  return 'success';
}

function expireLabel(dateStr) {
  if (!dateStr) return '—';
  const d = dayjs(dateStr);
  if (d.isBefore(dayjs())) return `Expired ${d.fromNow()}`;
  return formatDate(dateStr);
}

const CHART_COLORS = ['#1a6b2a', '#2e9e42', '#52c41a', '#73d13d', '#95de64', '#b7eb8f'];

function CustomBarLabel({ x, y, width, value }) {
  if (!value) return null;
  return (
    <text x={x + width / 2} y={y - 4} fill="#555" textAnchor="middle" fontSize={10}>
      {value}
    </text>
  );
}

export default function AdminSubscriptions() {
  const [data,       setData]       = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [pagination, setPagination] = useState({ total: 0, current_page: 1, per_page: 15, last_page: 1 });

  // Chart data (all matching rows, not paginated)
  const [chartData,     setChartData]     = useState([]);
  const [chartsLoading, setChartsLoading] = useState(false);

  // Filters
  const [name,          setName]          = useState('');
  const [mobile,        setMobile]        = useState('');
  const [subId,         setSubId]         = useState('');
  const [webAccess,     setWebAccess]     = useState('');
  const [referedBy,     setReferedBy]     = useState('');
  const [primeRange,    setPrimeRange]    = useState(null);
  const [expireRange,   setExpireRange]   = useState(null);
  const [expiredOnly,   setExpiredOnly]   = useState(false);
  const [remindingIds,  setRemindingIds]  = useState({});

  // Debounce timers
  const debounceRef      = useRef(null);
  const chartDebounceRef = useRef(null);

  const buildParams = useCallback((page = 1) => {
    const p = { page, per_page: pagination.per_page };
    if (name)        p.name            = name;
    if (mobile)      p.mobile          = mobile;
    if (subId)       p.subscription_id = subId;
    if (webAccess !== '') p.web_access = webAccess;
    if (referedBy)   p.refered_by      = referedBy;
    if (primeRange?.[0])  p.prime_from  = primeRange[0].format('YYYY-MM-DD');
    if (primeRange?.[1])  p.prime_to    = primeRange[1].format('YYYY-MM-DD');
    if (expireRange?.[0]) p.expire_from = expireRange[0].format('YYYY-MM-DD');
    if (expireRange?.[1]) p.expire_to   = expireRange[1].format('YYYY-MM-DD');
    if (expiredOnly)      p.expired_only = 1;
    return p;
  }, [name, mobile, subId, webAccess, referedBy, primeRange, expireRange, expiredOnly, pagination.per_page]);

  const fetchData = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const res = await getSubscriptionsApi(buildParams(page));
      setData(res.data?.data || []);
      const pg = res.data?.pagination || {};
      setPagination((prev) => ({
        ...prev,
        total:        pg.total        ?? prev.total,
        current_page: pg.current_page ?? page,
        last_page:    pg.last_page    ?? prev.last_page,
        per_page:     pg.per_page     ?? prev.per_page,
      }));
    } catch {
      // silent — table just stays empty
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  const fetchChartData = useCallback(async () => {
    setChartsLoading(true);
    try {
      const params = { ...buildParams(1), per_page: 9999 };
      const res = await getSubscriptionsApi(params);
      setChartData(res.data?.data || []);
    } catch {
      // silent
    } finally {
      setChartsLoading(false);
    }
  }, [buildParams]);

  // Debounce text filter changes
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchData(1), 400);
    return () => clearTimeout(debounceRef.current);
  }, [name, mobile, subId, referedBy, fetchData]);

  // Debounce chart data fetch (slightly longer delay)
  useEffect(() => {
    if (chartDebounceRef.current) clearTimeout(chartDebounceRef.current);
    chartDebounceRef.current = setTimeout(() => fetchChartData(), 600);
    return () => clearTimeout(chartDebounceRef.current);
  }, [name, mobile, subId, referedBy, webAccess, primeRange, expireRange, expiredOnly, fetchChartData]);

  // Immediate fetch for select/date/switch filters
  useEffect(() => { fetchData(1); }, [webAccess, primeRange, expireRange, expiredOnly]);

  // ── Stats from current page ───────────────────────────────────────────────
  const webAccessCount = data.filter((d) => d.web_access === 1).length;
  const expiredCount   = data.filter((d) => d.expireOn && dayjs(d.expireOn).isBefore(dayjs())).length;

  // ── Chart computations (from all rows, not just current page) ─────────────
  const monthlyData = useMemo(() => {
    const counts = {};
    chartData.forEach((r) => {
      if (!r.primeOn) return;
      const key = dayjs(r.primeOn).format('MMM YY');
      counts[key] = (counts[key] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([month, count]) => ({ month, count }))
      .sort((a, b) => dayjs(a.month, 'MMM YY').valueOf() - dayjs(b.month, 'MMM YY').valueOf());
  }, [chartData]);

  const weekOfMonthData = useMemo(() => {
    const buckets = [
      { week: 'Wk 1  (1–7)',   count: 0 },
      { week: 'Wk 2  (8–14)',  count: 0 },
      { week: 'Wk 3 (15–21)', count: 0 },
      { week: 'Wk 4 (22–31)', count: 0 },
    ];
    chartData.forEach((r) => {
      if (!r.primeOn) return;
      const day = dayjs(r.primeOn).date();
      if      (day <= 7)  buckets[0].count++;
      else if (day <= 14) buckets[1].count++;
      else if (day <= 21) buckets[2].count++;
      else                buckets[3].count++;
    });
    return buckets;
  }, [chartData]);

  const dayOfMonthData = useMemo(() => {
    const counts = {};
    for (let i = 1; i <= 31; i++) counts[i] = 0;
    chartData.forEach((r) => {
      if (!r.primeOn) return;
      const day = dayjs(r.primeOn).date();
      if (day >= 1 && day <= 31) counts[day]++;
    });
    return Array.from({ length: 31 }, (_, i) => ({ day: String(i + 1), count: counts[i + 1] }));
  }, [chartData]);

  // ── Columns ───────────────────────────────────────────────────────────────
  const columns = [
    {
      title: 'User',
      key: 'user',
      render: (_, r) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Avatar
            src={r.image && r.image !== 'null' ? (r.image.startsWith('http') ? r.image : USER_IMAGE(r.image)) : null}
            icon={<UserOutlined />}
            size={36}
            style={{ background: PRIMARY, flexShrink: 0 }}
          />
          <div>
            <div style={{ fontWeight: 600, fontSize: 13 }}>{r.name || '—'}</div>
            <div style={{ fontSize: 11, color: '#8c8c8c' }}>
              Joined {r.user_created_at ? dayjs(r.user_created_at).format('DD MMM YYYY') : '—'}
            </div>
          </div>
        </div>
      ),
      width: 200,
    },
    {
      title: 'Mobile',
      dataIndex: 'mobile',
      render: (v, r) => (
        <div>
          <div style={{ fontFamily: 'monospace' }}>{v || '—'}</div>
          <div style={{ fontSize: 11, color: '#8c8c8c' }}>ID: {r.user_id}</div>
        </div>
      ),
      width: 130,
    },
    {
      title: 'Subscription ID',
      dataIndex: 'subscription_id',
      render: (v) => v
        ? <Tag color="blue" style={{ fontFamily: 'monospace', fontSize: 11 }}>{v}</Tag>
        : <Text type="secondary">—</Text>,
      width: 150,
    },
    {
      title: 'Prime On',
      dataIndex: 'primeOn',
      render: (v) => v ? (
        <span style={{ fontSize: 12 }}>
          <CrownOutlined style={{ color: '#faad14', marginRight: 4 }} />
          {dayjs(v).format('DD MMM YYYY')}
        </span>
      ) : <Text type="secondary">—</Text>,
      width: 140,
    },
    {
      title: 'Expires On',
      dataIndex: 'expireOn',
      render: (v) => v ? (
        <Tooltip title={v}>
          <Tag color={expireColor(v)} style={{ fontSize: 11 }}>
            {expireLabel(v)}
          </Tag>
        </Tooltip>
      ) : <Text type="secondary">—</Text>,
      width: 150,
      sorter: (a, b) => dayjs(a.expireOn).unix() - dayjs(b.expireOn).unix(),
    },
    {
      title: 'Web Access',
      dataIndex: 'web_access',
      width: 110,
      render: (v) => v === 1
        ? <Tag icon={<CheckCircleOutlined />} color="success">Yes</Tag>
        : <Tag icon={<CloseCircleOutlined />} color="default">No</Tag>,
    },
    {
      title: 'Joined On',
      dataIndex: 'user_created_at',
      width: 120,
      render: (v) => v
        ? <span style={{ fontSize: 12 }}>{dayjs(v).format('DD MMM YYYY')}</span>
        : <Text type="secondary">—</Text>,
      sorter: (a, b) => dayjs(a.user_created_at).unix() - dayjs(b.user_created_at).unix(),
    },
    {
      title: 'Referred By',
      dataIndex: 'refered_by',
      width: 120,
      render: (v) => v
        ? <Tag color="purple" style={{ fontSize: 11 }}>{v}</Tag>
        : <Text type="secondary">—</Text>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 170,
      render: (_, r) => {
        if (expireColor(r.expireOn) !== 'error') return <Text type="secondary">—</Text>;

        if (r.subscribe_reminder_sent_at) {
          return (
            <Tooltip title={dayjs(r.subscribe_reminder_sent_at).format('DD MMM YYYY, h:mm A')}>
              <Tag icon={<CheckCircleOutlined />} color="success" style={{ fontSize: 11 }}>
                Reminded {dayjs(r.subscribe_reminder_sent_at).fromNow()}
              </Tag>
            </Tooltip>
          );
        }

        return (
          <Popconfirm
            title="Send WhatsApp reminder?"
            description={`Nudge ${r.name || 'this user'} to renew their subscription.`}
            onConfirm={() => handleRemindSubscribe(r)}
            okText="Send"
          >
            <Button
              size="small"
              icon={<WhatsAppOutlined />}
              loading={!!remindingIds[r.user_id]}
            >
              Remind to Subscribe
            </Button>
          </Popconfirm>
        );
      },
    },
  ];

  const clearFilters = () => {
    setName(''); setMobile(''); setSubId('');
    setWebAccess(''); setReferedBy('');
    setPrimeRange(null); setExpireRange(null);
    setExpiredOnly(false);
  };

  const hasFilters = name || mobile || subId || webAccess || referedBy || primeRange || expireRange || expiredOnly;

  const markReminded = (userId, sentAt) => {
    setData((prev) => prev.map((r) => (
      r.user_id === userId ? { ...r, subscribe_reminder_sent_at: sentAt } : r
    )));
  };

  const handleRemindSubscribe = async (row) => {
    setRemindingIds((prev) => ({ ...prev, [row.user_id]: true }));
    try {
      const res = await remindSubscribeApi({ user_id: row.user_id });
      if (res.data?.status === 'success') {
        message.success(`Reminder sent to ${row.name || 'user'}.`);
        markReminded(row.user_id, new Date().toISOString());
      } else {
        message.error(res.data?.message || 'Failed to send reminder.');
      }
    } catch (err) {
      // 409 = already reminded earlier; sync local state instead of retrying.
      if (err.response?.status === 409) {
        message.warning(err.response.data?.message || 'Reminder already sent.');
        markReminded(row.user_id, err.response.data?.sent_at || new Date().toISOString());
      } else {
        message.error(err.response?.data?.message || 'Failed to send reminder.');
      }
    } finally {
      setRemindingIds((prev) => ({ ...prev, [row.user_id]: false }));
    }
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <Title level={4} style={{ margin: 0, color: PRIMARY }}>
          <CrownOutlined style={{ marginRight: 8 }} />
          Subscriptions
        </Title>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => fetchData(pagination.current_page)} loading={loading} />
        </Space>
      </div>

      {/* Summary Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        <Col xs={24} sm={8}>
          <Card style={{ borderRadius: 10, border: '1px solid #f0f0f0' }} styles={{ body: { padding: '14px 20px' } }}>
            <Statistic
              title="Total Subscriptions"
              value={pagination.total}
              prefix={<CrownOutlined />}
              valueStyle={{ color: PRIMARY }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card style={{ borderRadius: 10, border: '1px solid #b7eb8f', background: '#f6ffed' }} styles={{ body: { padding: '14px 20px' } }}>
            <Statistic
              title="Web Access (this page)"
              value={webAccessCount}
              prefix={<GlobalOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card style={{ borderRadius: 10, border: '1px solid #ffccc7', background: '#fff2f0' }} styles={{ body: { padding: '14px 20px' } }}>
            <Statistic
              title="Expired (this page)"
              value={expiredCount}
              prefix={<CloseCircleOutlined />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
      </Row>

      {/* ── Analytics Charts ─────────────────────────────────────────── */}
      <Card
        style={{ borderRadius: 10, marginBottom: 16 }}
        styles={{ body: { padding: '16px 20px' } }}
        title={
          <Space>
            <BarChartOutlined style={{ color: PRIMARY }} />
            <span style={{ fontWeight: 600, fontSize: 14 }}>Subscription Analytics</span>
            {chartsLoading && <Spin size="small" />}
            {!chartsLoading && chartData.length > 0 && (
              <Tag color="green" style={{ marginLeft: 4 }}>{chartData.length} records</Tag>
            )}
          </Space>
        }
      >
        <Row gutter={[24, 24]}>
          {/* Monthly timeline */}
          <Col xs={24} lg={12}>
            <div style={{ marginBottom: 8 }}>
              <Text strong style={{ fontSize: 13 }}>Subscriptions by Month</Text>
              <div style={{ fontSize: 11, color: '#9ca3af' }}>Which months had the most sign-ups</div>
            </div>
            {chartsLoading ? (
              <div style={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Spin />
              </div>
            ) : monthlyData.length === 0 ? (
              <div style={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#bbb' }}>
                No data
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={monthlyData} margin={{ top: 18, right: 8, left: -20, bottom: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 10 }}
                    angle={-45}
                    textAnchor="end"
                    interval={0}
                  />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                  <ChartTooltip
                    formatter={(val) => [val, 'Subscriptions']}
                    contentStyle={{ fontSize: 12, borderRadius: 6 }}
                  />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]} maxBarSize={40}>
                    {monthlyData.map((entry, idx) => (
                      <Cell
                        key={entry.month}
                        fill={entry.count === Math.max(...monthlyData.map((d) => d.count)) ? '#1a6b2a' : '#52c41a'}
                      />
                    ))}
                    <CustomBarLabel />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Col>

          {/* Week of month */}
          <Col xs={24} sm={12} lg={6}>
            <div style={{ marginBottom: 8 }}>
              <Text strong style={{ fontSize: 13 }}>By Week of Month</Text>
              <div style={{ fontSize: 11, color: '#9ca3af' }}>Across all months combined</div>
            </div>
            {chartsLoading ? (
              <div style={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Spin />
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={weekOfMonthData} margin={{ top: 18, right: 8, left: -20, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="week" tick={{ fontSize: 9 }} />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                  <ChartTooltip
                    formatter={(val) => [val, 'Subscriptions']}
                    contentStyle={{ fontSize: 12, borderRadius: 6 }}
                  />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]} maxBarSize={56}>
                    {weekOfMonthData.map((entry, idx) => (
                      <Cell key={entry.week} fill={CHART_COLORS[idx] || CHART_COLORS[0]} />
                    ))}
                    <CustomBarLabel />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Col>

          {/* Day of month */}
          <Col xs={24} sm={12} lg={6}>
            <div style={{ marginBottom: 8 }}>
              <Text strong style={{ fontSize: 13 }}>By Day of Month</Text>
              <div style={{ fontSize: 11, color: '#9ca3af' }}>Which date drives the most sign-ups</div>
            </div>
            {chartsLoading ? (
              <div style={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Spin />
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={dayOfMonthData} margin={{ top: 18, right: 4, left: -20, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="day"
                    tick={{ fontSize: 8 }}
                    interval={2}
                  />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                  <ChartTooltip
                    formatter={(val, _name, props) => [val, `Day ${props.payload?.day}`]}
                    contentStyle={{ fontSize: 12, borderRadius: 6 }}
                  />
                  <Bar dataKey="count" radius={[3, 3, 0, 0]} maxBarSize={16}>
                    {dayOfMonthData.map((entry) => (
                      <Cell
                        key={entry.day}
                        fill={entry.count === Math.max(...dayOfMonthData.map((d) => d.count)) ? '#1a6b2a' : '#73d13d'}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Col>
        </Row>
      </Card>

      {/* Filters */}
      <Card style={{ borderRadius: 10, marginBottom: 16 }} styles={{ body: { padding: '14px 16px' } }}>
        <Row gutter={[12, 12]}>
          <Col xs={24} sm={12} md={6}>
            <Input
              placeholder="Search by name"
              prefix={<SearchOutlined />}
              value={name}
              onChange={(e) => setName(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Input
              placeholder="Search by mobile"
              prefix={<SearchOutlined />}
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Input
              placeholder="Subscription ID"
              value={subId}
              onChange={(e) => setSubId(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Input
              placeholder="Referred by"
              value={referedBy}
              onChange={(e) => setReferedBy(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              value={webAccess}
              onChange={setWebAccess}
              options={WEB_ACCESS_OPTIONS}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={12} md={9}>
            <RangePicker
              value={primeRange}
              onChange={setPrimeRange}
              format="DD MMM YYYY"
              style={{ width: '100%' }}
              placeholder={['Prime from', 'Prime to']}
            />
          </Col>
          <Col xs={24} sm={12} md={9}>
            <RangePicker
              value={expireRange}
              onChange={setExpireRange}
              format="DD MMM YYYY"
              style={{ width: '100%' }}
              placeholder={['Expire from', 'Expire to']}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Space>
              <Switch checked={expiredOnly} onChange={setExpiredOnly} />
              <Text style={{ fontSize: 13 }}>Expired only</Text>
            </Space>
          </Col>
          {hasFilters && (
            <Col xs={24} sm={12} md={24} style={{ textAlign: 'right' }}>
              <Button onClick={clearFilters}>Clear Filters</Button>
            </Col>
          )}
        </Row>
      </Card>

      {/* Table */}
      <Card style={{ borderRadius: 10 }}>
        {loading && data.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>
        ) : (
          <Table
            dataSource={data}
            columns={columns}
            rowKey="user_id"
            loading={loading}
            scroll={{ x: 1070 }}
            pagination={{
              current:   pagination.current_page,
              pageSize:  pagination.per_page,
              total:     pagination.total,
              showSizeChanger: true,
              showTotal: (total, range) => `${range[0]}–${range[1]} of ${total}`,
              onChange:  (page, pageSize) => {
                setPagination((p) => ({ ...p, per_page: pageSize }));
                fetchData(page);
              },
            }}
          />
        )}
      </Card>
    </div>
  );
}
